import { contextIsAvailable } from '@aboutyou/legacy-shared/src/tracking/hooks/trackingHelpers'
import { TrackingProductStreamSortType } from '@aboutyou/legacy-tracking-sdk'
import { GENDER_ROOT_CATEGORIES } from 'constants/categories'
import { useTranslation } from 'helpers/hooks/useTranslation'
import { useRouter } from 'helpers/navigation'
import { useTracking } from 'helpers/tracking/hooks/useTrackingContextAndClientV6'
import { useObserver } from 'mobx-react-lite'
import React, { FC, memo, useCallback, useMemo } from 'react'
import GenderSwitch from 'sharedComponents/ProductStream/GenderSwitch'
import {
  useImageViewStore,
  useLayoutStore,
  useLocaleStore,
  useUrlManagerStore,
} from 'store/context'
import ImageViewStore, { ImageType } from 'store/features/imageView'
import { useActiveImageType } from 'store/features/imageView/context'
import {
  useCategoryStore,
  usePaginationStore,
  useSortingStore,
} from 'store/sidecar/ProductStreamContext'
import styled from 'styled-components'
import { IconName } from 'uiComponents/atoms/Icon/IconName'
import NumberBadge from 'uiComponents/atoms/NumberBadge/NumberBadge'
import { HeadingM } from 'uiComponents/atoms/Text/Headings'
import { bordersColor } from 'uiComponents/constants/colors'
import { mediaTabletAndDown } from 'uiComponents/constants/customMedia'
import { DropdownContentAlignment } from 'uiComponents/molecules/Dropdown/Dropdown'
import { DropdownOptionType } from 'uiComponents/molecules/Dropdown/types'
import HorizontalSortingDropdownFilter from 'uiComponents/organisms/Filters/HorizontalSortingDropdownFilter'
import { ProductSortingIdType } from '../../constants/sorting'
import {
  useTrackOnCloseFilter,
  useTrackOnOpenFilter,
} from '../../helpers/tracking/hooks/trackFilter'
import { ProductSorting } from '../../store/sidecar/SortingViewStore'

const HorizontalWrapper = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding-top: 4px;
  padding-bottom: 20px;

  @media ${mediaTabletAndDown} {
    display: block;
  }

  border-bottom: 1px solid ${bordersColor};
  margin-bottom: 20px;
`

const ControlWrapper = styled.div`
  display: flex;

  @media ${mediaTabletAndDown} {
    justify-content: flex-start;
    margin-top: 15px;
  }
`
const StyledHorizontalSortingDropdownFilter = styled(
  HorizontalSortingDropdownFilter,
)`
  margin-right: 10px;

  &:last-child {
    margin-right: 0;
  }
`

const StyledNumberBadge = styled(NumberBadge)`
  margin-left: 5px;
  height: 22px;
`

const StyledGenderSwitch = styled(GenderSwitch)`
  margin: 0 15px;
`

const StyledHeadingM = styled(HeadingM)`
  display: flex;
  align-items: center;
`

const useSortTracking = () => {
  const { TrackingClient, pageContext } = useTracking()

  return useCallback(
    (sorting: ProductSorting) => {
      const trackingSortType: TrackingProductStreamSortType | undefined =
        sortIdToSortType(sorting.id)

      if (
        trackingSortType &&
        contextIsAvailable(pageContext) &&
        !pageContext.disabled
      ) {
        TrackingClient?.trackProductListSort({ sort: trackingSortType })
      }
    },
    [TrackingClient, pageContext],
  )
}

function sortIdToSortType(sorting: ProductSortingIdType) {
  switch (sorting) {
    case 'reduction_highest':
      return 'reduction_highest'
    case 'topseller':
      return 'topseller'
    case 'new_first':
      return 'new_first'
    case 'price_high':
      return 'price_highest'
    case 'price_low':
      return 'price_lowest'
  }
}
const CategorySortingBar: FC<{ title?: string }> = memo(({ title }) => {
  const trackSorting = useSortTracking()
  const { t } = useTranslation()
  const { navigateClientWithParams } = useRouter()
  const trackOnCloseFilter = useTrackOnCloseFilter()
  const trackOnOpenFilter = useTrackOnOpenFilter()

  const imageViewStore = useImageViewStore()
  const urlManagerStore = useUrlManagerStore()
  const categoryStore = useCategoryStore()
  const paginationStore = usePaginationStore()
  const sortingStore = useSortingStore()
  const hrefLang = useLocaleStore(store => store.hrefLang)
  const layoutStore = useLayoutStore()

  const productCount = useObserver(() => paginationStore.refinedTotalCount)
  const categoryHeading = useObserver(() => {
    if (title) {
      return title
    }
    if (
      !categoryStore.id ||
      GENDER_ROOT_CATEGORIES.includes(categoryStore.id)
    ) {
      return undefined
    }
    return categoryStore.name
  })

  const sortingOptions = useObserver(() => sortingStore.productsSortings)
  const appliedProductsSorting = useObserver(
    () => sortingStore.appliedProductsSorting,
  )
  const activeImageType = useActiveImageType()
  const selectedCategoryIds = useObserver(() => categoryStore.selectedIds)

  const allowModelImageSelection =
    ImageViewStore.mayCategoriesDisplayModelImage(selectedCategoryIds)

  const hrefLangLocale = useMemo(() => hrefLang, [])

  const isOnlineMarketing = urlManagerStore.params.omUnisex === '1'

  const modifiedSortingOptions = sortingOptions.map(option => ({
    label: option.title,
    id: option.id,
  }))

  const imageViewOptions: DropdownOptionType[] = [
    {
      label: t('Produktansicht') as string,
      id: 'bust',
    },
    {
      label: t('Modelansicht') as string,
      id: 'model',
    },
  ]

  const selectedImageOption = imageViewOptions.find(
    option => option.id === activeImageType,
  )

  const selectedSortingOption = modifiedSortingOptions.find(
    option => option.id === appliedProductsSorting.id,
  )

  const { TrackingClient, pageContext } = useTracking()

  const onImageViewSelect = (value: ImageType) => {
    imageViewStore.updateUserImageType(value, { persist: true })

    const tileView = value === 'bust' ? 'product' : 'model'

    if (contextIsAvailable(pageContext) && !pageContext.disabled) {
      TrackingClient?.trackProductListTileView({ tileView })
    }
  }

  const onSortingSelect = (value: string) => {
    const sorting = sortingOptions.find(option => option.value === value)
    if (sorting) {
      const { firstProductId, page, ...params } = urlManagerStore.params

      trackSorting(sorting)
      navigateClientWithParams(urlManagerStore.pathname)({
        ...params,
        sort: sorting.id,
        page: undefined,
      })()
    }
  }

  const defaultImageType = ImageViewStore.getCategoryDefaultImageType(
    categoryStore.selectedIds,
    layoutStore.activeRouteName,
  )

  const renderCategoryHeading = (
    <>
      {categoryHeading}
      <StyledNumberBadge data-testid="ProductCount" type="disabled">
        {productCount.toLocaleString(hrefLangLocale)}
      </StyledNumberBadge>
    </>
  )

  const dropdownContentAlignment = DropdownContentAlignment.RIGHT

  const trackOnOpen = (filterName: string) => () => {
    trackOnOpenFilter(filterName, 'filter_modal')
  }
  const trackOnSortOpen = () => trackOnOpen('sort')
  const trackOnViewOtionsOpen = () => trackOnOpen('view')

  const trackOnClose = (filterName: string) => () => {
    trackOnCloseFilter(filterName, 'filter_modal')
  }
  const trackOnSortClose = () => trackOnClose('sort')
  const trackOnViewOtionsClose = () => trackOnClose('view')

  return (
    <HorizontalWrapper data-testid="CategorySortingBar">
      <div>
        <StyledHeadingM
          as={'span'}
          data-testid={title ? 'HeadingSeoKeyword' : 'HeadingCategoryName'}
        >
          {categoryHeading && renderCategoryHeading}
        </StyledHeadingM>
        {isOnlineMarketing && <StyledGenderSwitch />}
      </div>

      <ControlWrapper data-testid="ControlWrapper">
        {allowModelImageSelection && (
          <StyledHorizontalSortingDropdownFilter
            dataTestId="ModelVsBust"
            selectedOption={selectedImageOption!}
            onSelect={option => onImageViewSelect(option.id as ImageType)}
            options={imageViewOptions}
            isDefaultOptionSelected={defaultImageType === activeImageType}
            dropdownLabel={t('Ansicht')}
            dropdownIconName={IconName.IcShirt}
            contentAlignment={dropdownContentAlignment}
            onCloseWithoutOption={trackOnViewOtionsClose}
            onClose={trackOnViewOtionsClose}
            onOpen={trackOnViewOtionsOpen}
          />
        )}
        {
          <StyledHorizontalSortingDropdownFilter
            dataTestId="Sorting"
            selectedOption={selectedSortingOption!}
            onSelect={option => onSortingSelect(option.id)}
            options={modifiedSortingOptions}
            isDefaultOptionSelected={sortingStore.isDefaultSorting}
            dropdownLabel={t('Sortierung')}
            dropdownIconName={IconName.IcSort}
            contentAlignment={dropdownContentAlignment}
            onCloseWithoutOption={trackOnSortClose}
            onClose={trackOnSortClose}
            onOpen={trackOnSortOpen}
          />
        }
      </ControlWrapper>
    </HorizontalWrapper>
  )
})

CategorySortingBar.displayName = 'CategorySortingBar'

export default CategorySortingBar
